import 'what-input'
import * as React from 'react'
import {FC} from 'react'
import {render, unmountComponentAtNode} from 'react-dom'
import {autorun, configure, toJS} from 'mobx'
import {observer, Provider as MobXLegacyProvider} from 'mobx-react'
import {each} from 'lodash'
import cn from 'classnames'
import sizeMe from 'react-sizeme'
import {CSSTransition, TransitionGroup} from 'react-transition-group'
import {createEpik} from '@raisenow/epik'
import {useStores} from '../lib/MobXProvider'
import {createCompiler} from '../lib/Compiler'
import {createSubscriptionRenewForm} from '../lib/forms/SubscriptionRenewForm'
import {createWidgetApi, WidgetApi} from '../lib/WidgetApi'
import {
  createComputedConfig,
  createConfig,
  UiBreakpointsConfig,
  WidgetConfig,
} from '../lib/WidgetConfig'
import {CookieStore} from '../lib/CookieStore'
import {WidgetFlag, WidgetOverlayFlags} from '../lib/WidgetFlag'
import {createFlags} from '../lib/Flags'
import {createPaymentForm} from '../lib/forms/PaymentForm'
import {DynamicSlotName} from '../lib/DynamicSlotName'
import {createTranslator} from '../lib/Translator'
import {createValidator} from '../lib/Validator'
import {OverlayMode} from '../lib/OverlayMode'
import {createWidgetUi} from '../lib/WidgetUi'
import {useAlly} from '../lib/hooks/useAlly'
import {createEvents, WidgetEventName, WidgetEvents} from '../lib/WidgetEventer'
import {ErrorBoundary} from './ui/ErrorBoundary'
import {Debug} from './ui/Debug'
import {DynamicSlot} from './dynamic/DynamicSlot'
import {TransactionInfoWidget} from './widgets/transaction-info/TransactionInfoWidget'
import {SubscriptionInfoWidget} from './widgets/subscription-info/SubscriptionInfoWidget'
import {EpmsPaymentInfoWidget} from './widgets/epms-payment-info/EpmsPaymentInfoWidget'
import {EpmsSubscriptionInfoWidget} from './widgets/epms-subscription-info/EpmsSubscriptionInfoWidget'
import {EpmsPaymentResponseDataInfoBlock} from './widgets/epms-payment-response-data-info/EpmsPaymentResponseDataInfoBlock'
import {PaymentFormWidget} from './widgets/payment-form/PaymentFormWidget'
import {SpinnerInnerBlock} from './widgets/spinner/SpinnerInnerBlock'
import {SpinnerOuterBlock} from './widgets/spinner/SpinnerOuterBlock'
import {OverlayDataProtectionPolicyBlock} from './widgets/overlay/OverlayDataProtectionPolicyBlock'
import {OverlaySmsPollBlock} from './widgets/overlay/OverlaySmsPollBlock'

///////////////////////////////////////////////////////////////////////////////

enum TopLevelBlock {
  SPINNER_INNER_BLOCK = 'spinner_inner_block',
  OVERLAY_SMS_POLL_BLOCK = 'overlay_sms_poll_block',
  OVERLAY_DATA_PROTECTION_POLICY_BLOCK = 'overlay_data_protection_policy_block',
  TRANSACTION_INFO_WIDGET = 'transaction_info_widget',
  SUBSCRIPTION_INFO_WIDGET = 'subscription_info_widget',
  EPMS_PAYMENT_INFO_WIDGET = 'epms_payment_info_widget',
  EPMS_SUBSCRIPTION_INFO_WIDGET = 'epms_subscription_info_widget',
  EPMS_PAYMENT_RESPONSE_DATA_INFO_BLOCK = 'epms_payment_response_data_info_block',
  PAYMENT_FORM_WIDGET = 'payment_form_widget',
}

export type WidgetProps = {
  size: {
    width: number
  }
}

export type CreateWidgetArgs = {
  widgetConfig?: WidgetConfig
  events?: WidgetEvents
}

///////////////////////////////////////////////////////////////////////////////

// // https://github.com/mobxjs/mobx/issues/1082
// extras.isolateGlobalState()
configure({isolateGlobalState: true})

///////////////////////////////////////////////////////////////////////////////

const getBreakpointsClasses = (
  width: number,
  uiBreakpoints: UiBreakpointsConfig,
): string[] => {
  let rootFontSize =
    parseInt(
      window.getComputedStyle(document.body).getPropertyValue('font-size'),
    ) || 16
  let breakpointsClasses = []

  each(uiBreakpoints, (v, k) => {
    if (width / rootFontSize >= v) {
      breakpointsClasses.push(k)
    }
  })

  return breakpointsClasses
}

///////////////////////////////////////////////////////////////////////////////

const Widget: FC<WidgetProps> = observer((props) => {
  const {api} = useStores<{api: WidgetApi}>()
  const {
    size: {width},
  } = props
  const {flags} = api
  const {
    debug,
    overlayMode,
    uiTransitionTimeout: timeout,
    uiBreakpoints,
  } = api.computedConfig
  const breakpointsClasses = getBreakpointsClasses(width, toJS(uiBreakpoints))
  const widgetTopId = api.ui.getTopElementId()
  const title = api.trans('title', {defaultValue: null})

  let showWidgetHeader = true

  if (flags.has(WidgetOverlayFlags) && overlayMode === OverlayMode.INNER) {
    showWidgetHeader = false
  }

  const isSpinnerInnerShown = flags.has(WidgetFlag.SHOW_SPINNER_INNER)
  const isSpinnerOuterShown = flags.has(WidgetFlag.SHOW_SPINNER_OUTER)

  const isOverlaySmsPollShown = flags.has(WidgetFlag.SHOW_OVERLAY_SMS_POLL)
  const isOverlayDataProtectionPoliceyShown = flags.has(
    WidgetFlag.SHOW_OVERLAY_DATA_PROTECTION_POLICY,
  )

  const isSubscriptionInfoWidget =
    flags.has(WidgetFlag.HAS_SUBSCRIPTION_INFO) ||
    flags.has(WidgetFlag.SUBSCRIPTION_NOT_FOUND)

  const isTransactionInfoWidget =
    flags.has(WidgetFlag.HAS_TRANSACTION_INFO) ||
    flags.has(WidgetFlag.TRANSACTION_NOT_FOUND) ||
    flags.has(WidgetFlag.PAYMENT_ABORTED)

  const isEpmsPaymentInfoWidget =
    flags.has(WidgetFlag.HAS_EPMS_PAYMENT_INFO) ||
    flags.has(WidgetFlag.EPMS_PAYMENT_NOT_FOUND) ||
    flags.has(WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_ERROR)

  const isEpmsSubscriptionInfoWidget =
    flags.has(WidgetFlag.HAS_EPMS_SUBSCRIPTION_INFO) ||
    flags.has(WidgetFlag.EPMS_SUBSCRIPTION_NOT_FOUND)

  const isEpmsPaymentResponseDataInfoBlock = !!flags.has(
    WidgetFlag.HAS_EPMS_PAYMENT_RESPONSE_DATA,
  )

  const isPaymentFormWidget =
    !isTransactionInfoWidget &&
    !isSubscriptionInfoWidget &&
    !isEpmsPaymentInfoWidget &&
    !isEpmsSubscriptionInfoWidget &&
    !isEpmsPaymentResponseDataInfoBlock

  const widgets = {
    [TopLevelBlock.SPINNER_INNER_BLOCK]: SpinnerInnerBlock,
    [TopLevelBlock.OVERLAY_SMS_POLL_BLOCK]: OverlaySmsPollBlock,
    [TopLevelBlock.OVERLAY_DATA_PROTECTION_POLICY_BLOCK]: OverlayDataProtectionPolicyBlock,
    [TopLevelBlock.TRANSACTION_INFO_WIDGET]: TransactionInfoWidget,
    [TopLevelBlock.SUBSCRIPTION_INFO_WIDGET]: SubscriptionInfoWidget,
    [TopLevelBlock.EPMS_PAYMENT_INFO_WIDGET]: EpmsPaymentInfoWidget,
    [TopLevelBlock.EPMS_SUBSCRIPTION_INFO_WIDGET]: EpmsSubscriptionInfoWidget,
    [TopLevelBlock.EPMS_PAYMENT_RESPONSE_DATA_INFO_BLOCK]: EpmsPaymentResponseDataInfoBlock,
    [TopLevelBlock.PAYMENT_FORM_WIDGET]: PaymentFormWidget,
  }

  let key = isSpinnerInnerShown ? TopLevelBlock.SPINNER_INNER_BLOCK : null

  if (overlayMode === OverlayMode.INNER) {
    key =
      !key && isOverlaySmsPollShown ? TopLevelBlock.OVERLAY_SMS_POLL_BLOCK : key
    key =
      !key && isOverlayDataProtectionPoliceyShown
        ? TopLevelBlock.OVERLAY_DATA_PROTECTION_POLICY_BLOCK
        : key
  }

  key =
    !key && isSubscriptionInfoWidget
      ? TopLevelBlock.SUBSCRIPTION_INFO_WIDGET
      : key
  key =
    !key && isTransactionInfoWidget
      ? TopLevelBlock.TRANSACTION_INFO_WIDGET
      : key
  key =
    !key && isEpmsSubscriptionInfoWidget
      ? TopLevelBlock.EPMS_SUBSCRIPTION_INFO_WIDGET
      : key
  key =
    !key && isEpmsPaymentInfoWidget
      ? TopLevelBlock.EPMS_PAYMENT_INFO_WIDGET
      : key
  key =
    !key && isEpmsPaymentResponseDataInfoBlock
      ? TopLevelBlock.EPMS_PAYMENT_RESPONSE_DATA_INFO_BLOCK
      : key
  key = !key && isPaymentFormWidget ? TopLevelBlock.PAYMENT_FORM_WIDGET : key

  const Widget = widgets[key] || (() => null)

  useAlly()

  return (
    <section
      id="tamaro-widget"
      className={cn('tamaro-widget', breakpointsClasses)}
    >
      <div id={widgetTopId} />

      <DynamicSlot name={DynamicSlotName.START} />

      {showWidgetHeader && (
        <div className="widget-header">
          <div className="image" />
          {title && (
            <div className="title">
              <h2 className="text">{title}</h2>
            </div>
          )}
        </div>
      )}

      <div className="widget-content" ref={api.widgetContentRef}>
        <TransitionGroup component={null}>
          <CSSTransition
            key={key}
            timeout={timeout}
            appear
            classNames="fade-in"
          >
            <Widget />
          </CSSTransition>
        </TransitionGroup>

        {isSpinnerOuterShown && <SpinnerOuterBlock />}

        <CSSTransition
          in={overlayMode === OverlayMode.OUTER && isOverlaySmsPollShown}
          mountOnEnter
          unmountOnExit
          timeout={timeout}
          classNames="overlay-slide"
        >
          <OverlaySmsPollBlock />
        </CSSTransition>

        <CSSTransition
          in={
            overlayMode === OverlayMode.OUTER &&
            isOverlayDataProtectionPoliceyShown
          }
          mountOnEnter
          unmountOnExit
          timeout={timeout}
          classNames="overlay-slide"
        >
          <OverlayDataProtectionPolicyBlock />
        </CSSTransition>
      </div>

      <DynamicSlot name={DynamicSlotName.END} />

      {debug && <Debug />}
    </section>
  )
})

export async function createWidget(args: CreateWidgetArgs): Promise<WidgetApi> {
  let {widgetConfig, events} = args
  widgetConfig = widgetConfig || {}
  events = events || createEvents()

  events[WidgetEventName.BEFORE_CREATE] &&
    (await events[WidgetEventName.BEFORE_CREATE].publish())

  let compiler = createCompiler()
  let config = createConfig(widgetConfig)
  let translator = createTranslator({config, compiler})
  let computedConfig = createComputedConfig(config, translator)

  let validator = createValidator({compiler})
  let epik = createEpik(computedConfig.epikConfig)
  let flags = createFlags()
  let ui = createWidgetUi()
  let paymentFormStore = new CookieStore('tamaro-widget-payment-form-widget', 2)
  let paymentForm = createPaymentForm()
  let subscriptionRenewForm = createSubscriptionRenewForm()

  autorun(() => {
    translator.debug = computedConfig.debug
    epik.config.debug = computedConfig.debug
    epik.config.language = computedConfig.language
  })

  let api = createWidgetApi({
    config,
    computedConfig,
    events,
    compiler,
    translator,
    validator,
    epik,
    ui,
    flags,
    paymentForm,
    subscriptionRenewForm,
  })

  await ui.init({api})
  await paymentForm.init({api, store: paymentFormStore})
  await subscriptionRenewForm.init({api})
  await api.init()

  const WrappedWidget = sizeMe()(Widget)

  api.node = (
    <MobXLegacyProvider api={api}>
      <ErrorBoundary>
        <WrappedWidget />
      </ErrorBoundary>
    </MobXLegacyProvider>
  )

  await events[WidgetEventName.AFTER_CREATE].publish({api})

  return api
}

export async function renderWidget(
  api: WidgetApi,
  targetSelector: string,
): Promise<void> {
  await api.events[WidgetEventName.BEFORE_RENDER].publish({api})

  let target = document.querySelector(targetSelector)
  assertTarget(targetSelector)

  return new Promise<void>((resolve) => {
    render(api.node, target, async () => {
      await api.events[WidgetEventName.AFTER_RENDER].publish({api})
      resolve()
    })
  })
}

export function removeWidget(targetSelector: string) {
  let target = document.querySelector(targetSelector)
  assertTarget(targetSelector)
  unmountComponentAtNode(target)
}

export function assertTarget(targetSelector: string) {
  let target = document.querySelector(targetSelector)

  if (!target) {
    throw new Error(`Could not find target "${targetSelector}"`)
  }
}
